import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { formatTL } from "../lib/format";
import { MALIYET_KATEGORILERI, BOLUMLER, bugun } from "../lib/sabitler";
import { Loader2 } from "lucide-react";

const EMPTY = {
  tarih: "",
  plaka_dosya_no: "",
  kategori: "",
  tedarikci: "",
  matrah: "",
  kdv: "",
  belge_no: "",
  bolum: "",
  aciklama: "",
};

export const MaliyetForm = ({ initial, onSubmit, submitting, submitLabel = "Kaydet" }) => {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    tarih: bugun(),
    ...(initial
      ? {
          ...initial,
          matrah: initial.matrah != null ? String(initial.matrah) : "",
          kdv: initial.kdv != null ? String(initial.kdv) : "",
          plaka_dosya_no: initial.plaka_dosya_no || "",
          tedarikci: initial.tedarikci || "",
          belge_no: initial.belge_no || "",
          aciklama: initial.aciklama || "",
          kategori: initial.kategori || "",
          bolum: initial.bolum || "",
        }
      : {}),
  }));

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toplam = useMemo(() => {
    return (parseFloat(form.matrah) || 0) + (parseFloat(form.kdv) || 0);
  }, [form.matrah, form.kdv]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.tarih) return toast.error("Tarih zorunludur.");
    const matrah = parseFloat(form.matrah);
    if (!matrah || matrah <= 0) return toast.error("Matrah 0'dan büyük olmalıdır.");
    if (!form.kategori) return toast.error("Maliyet kategorisi seçin.");
    if (!form.bolum) return toast.error("Bölüm seçin.");

    onSubmit({
      tarih: form.tarih,
      plaka_dosya_no: form.plaka_dosya_no.trim() || null,
      tedarikci: form.tedarikci.trim(),
      matrah,
      kdv: parseFloat(form.kdv) || 0,
      belge_no: form.belge_no.trim(),
      aciklama: form.aciklama.trim(),
      kategori: form.kategori,
      bolum: form.bolum,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="maliyet-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Tarih *</Label>
          <Input
            type="date"
            value={form.tarih}
            onChange={(e) => set("tarih", e.target.value)}
            className="h-12"
            data-testid="maliyet-form-tarih-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Plaka / Dosya No <span className="text-xs text-muted-foreground">(3A DYS anahtarı)</span></Label>
          <Input
            placeholder="34 ABC 123 / HD-2026-041"
            value={form.plaka_dosya_no}
            onChange={(e) => set("plaka_dosya_no", e.target.value)}
            className="h-12"
            data-testid="maliyet-form-plaka-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Maliyet Kategorisi *</Label>
          <Select value={form.kategori} onValueChange={(v) => set("kategori", v)}>
            <SelectTrigger className="h-12" data-testid="maliyet-form-kategori-select">
              <SelectValue placeholder="Seçin" />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {MALIYET_KATEGORILERI.map((k) => (
                <SelectItem key={k} value={k}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Bölüm *</Label>
          <Select value={form.bolum} onValueChange={(v) => set("bolum", v)}>
            <SelectTrigger className="h-12" data-testid="maliyet-form-bolum-select">
              <SelectValue placeholder="Seçin" />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {BOLUMLER.map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Tedarikçi</Label>
          <Input
            placeholder="Tedarikçi adı"
            value={form.tedarikci}
            onChange={(e) => set("tedarikci", e.target.value)}
            className="h-12"
            data-testid="maliyet-form-tedarikci-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Matrah (KDV hariç) * ₺</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0,00"
            value={form.matrah}
            onChange={(e) => set("matrah", e.target.value)}
            className="h-12 text-right tabular-nums"
            data-testid="maliyet-form-matrah-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>KDV ₺</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0,00"
            value={form.kdv}
            onChange={(e) => set("kdv", e.target.value)}
            className="h-12 text-right tabular-nums"
            data-testid="maliyet-form-kdv-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Belge No</Label>
          <Input
            placeholder="ALF2026..."
            value={form.belge_no}
            onChange={(e) => set("belge_no", e.target.value)}
            className="h-12"
            data-testid="maliyet-form-belge-no-input"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Açıklama</Label>
          <Input
            placeholder="Ön tampon + far vb."
            value={form.aciklama}
            onChange={(e) => set("aciklama", e.target.value)}
            className="h-12"
            data-testid="maliyet-form-aciklama-input"
          />
        </div>
      </div>

      <div
        className="flex items-center justify-between rounded-lg bg-[hsl(var(--primary))] px-4 py-3 text-white"
        data-testid="maliyet-form-toplam-display"
      >
        <span className="text-sm text-white/70">Toplam (Matrah + KDV)</span>
        <span className="font-display text-xl font-bold tabular-nums text-[hsl(var(--accent))]">
          {formatTL(toplam, 2)}
        </span>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="h-12 w-full sm:w-auto sm:min-w-[200px] rounded-lg text-base font-medium transition-colors duration-150 active:scale-[0.98]"
        data-testid="maliyet-form-submit-button"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Kaydediliyor…
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
};
